/**
 * NavigatorX Service Layer
 * 
 * Central service for all NavigatorX SuperAdmin operations.
 * This service provides a clean API for managing NavigatorX users,
 * subscriptions, referrals, hazards, and more.
 * 
 * IMPORTANT: Firestore Schema
 * - nav_users/{userId} - All user data (profile, subscription, settings)
 * - nav_users/{userId}/favorites/{wellId} - Saved wells
 * - nav_users/{userId}/well_history/{wellId} - Recently visited wells
 * - nav_users/{userId}/referral_invites/{inviteId} - Sent invites
 * - nav_users/{userId}/referrals/{referralId} - Referral tracking
 * - nav_users/{userId}/notifications/{notifId} - In-app notifications
 * - nav_users/{userId}/hazard_reports/{reportId} - User's hazard reports
 * - nav_users/{userId}/verification/{type} - Phone/email verification
 * - nav_users/blocked/emails/{hash} - Blocked email hashes
 * - nav_users/blocked/phones/{hash} - Blocked phone hashes
 * - nav_users/invites/pending/{token} - Pending invite tokens
 * - nav_users/admin_logs/{logId} - Admin audit logs
 * - hazards/{hazardId} - Global hazard collection
 * - waitlist/{documentId} - Waitlist signups
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  writeBatch,
  increment,
  arrayUnion,
  arrayRemove,
  DocumentSnapshot,
  QueryConstraint,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { navDb, functions, auth } from '../firebase/client';
import { httpsCallable } from 'firebase/functions';
import type {
  NavUser,
  NavUserSubscriptionStatus,
  NavUserCreateInput,
  NavUserUpdateInput,
  ReferralStats,
  ReferralInvite,
  Hazard,
  HazardType,
  WaitlistEntry,
  AdminAuditLog,
  AdminAction,
  NavigatorXStats,
  PaginatedResult,
  UserActivity
} from '../../types/navigatorx';
import { SUBSCRIPTION_PRICE } from './navigatorXBillingService';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Main NavigatorX users collection */
export const NAV_USERS_COLLECTION = 'nav_users';

/** Global hazards collection */
export const HAZARDS_COLLECTION = 'hazards';

/** Waitlist collection */
export const WAITLIST_COLLECTION = 'waitlist';

/** Blocked identities document */
export const BLOCKED_DOC = 'blocked';

/** Invites document */
export const INVITES_DOC = 'invites';

/** Admin logs document */
export const ADMIN_LOGS_DOC = 'admin_logs';

/** Subcollection names */
export const SUBCOLLECTIONS = {
  FAVORITES: 'favorites',
  WELL_HISTORY: 'well_history',
  REFERRAL_INVITES: 'referral_invites',
  REFERRALS: 'referrals',
  NOTIFICATIONS: 'notifications',
  HAZARD_REPORTS: 'hazard_reports',
  VERIFICATION: 'verification',
  CANCELLATION_FEEDBACK: 'cancellation_feedback'
} as const;

/** Subscription status values (matching Android app) */
export const SUBSCRIPTION_STATUS = {
  NONE: 'NONE',
  EMAIL_VERIFIED: 'EMAIL_VERIFIED',
  PHONE_VERIFIED: 'PHONE_VERIFIED',
  TRIAL_ACTIVE: 'TRIAL_ACTIVE',
  TRIAL_EXPIRED: 'TRIAL_EXPIRED',
  SUBSCRIBED: 'SUBSCRIBED',
  SUBSCRIPTION_EXPIRED: 'SUBSCRIPTION_EXPIRED',
  REFERRAL_FREE_MONTH: 'REFERRAL_FREE_MONTH',
  BLOCKED: 'BLOCKED',
  // Legacy statuses from web admin
  trial: 'trial',
  active: 'active',
  expired: 'expired',
  cancelled: 'cancelled',
  grace: 'grace',
  invited: 'invited'
} as const;

/** Default pagination limit */
const DEFAULT_PAGE_SIZE = 25;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get a reference to a user document
 */
export function userDocRef(userId: string) {
  return doc(navDb, NAV_USERS_COLLECTION, userId);
}

/**
 * Get a reference to a user's subcollection
 */
export function userSubcollectionRef(userId: string, subcollection: string) {
  return collection(navDb, NAV_USERS_COLLECTION, userId, subcollection);
}

/**
 * Get a reference to blocked emails collection
 */
export function blockedEmailsRef() {
  return collection(navDb, NAV_USERS_COLLECTION, BLOCKED_DOC, 'emails');
}

/**
 * Get a reference to blocked phones collection
 */
export function blockedPhonesRef() {
  return collection(navDb, NAV_USERS_COLLECTION, BLOCKED_DOC, 'phones');
}

/**
 * Get a reference to pending invites collection
 */
export function pendingInvitesRef() {
  return collection(navDb, NAV_USERS_COLLECTION, INVITES_DOC, 'pending');
}

/**
 * Get a reference to admin logs collection
 */
export function adminLogsRef() {
  return collection(navDb, NAV_USERS_COLLECTION, ADMIN_LOGS_DOC, 'entries');
}

/**
 * Convert Firestore timestamp to Date
 */
export function timestampToDate(timestamp: any): Date | null {
  if (!timestamp) return null;
  if (timestamp.toDate) return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'number') return new Date(timestamp);
  if (typeof timestamp === 'string') return new Date(timestamp);
  return null;
}

/**
 * Normalize subscription status (handle legacy values)
 */
export function normalizeSubscriptionStatus(status: string): NavUserSubscriptionStatus {
  const statusMap: Record<string, NavUserSubscriptionStatus> = {
    'trial': 'TRIAL_ACTIVE',
    'active': 'SUBSCRIBED',
    'expired': 'SUBSCRIPTION_EXPIRED',
    'cancelled': 'BLOCKED',
    'grace': 'SUBSCRIPTION_EXPIRED',
    'invited': 'NONE'
  };
  return statusMap[status] || (status as NavUserSubscriptionStatus);
}

/**
 * Generate a random alphanumeric string
 */
export function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

/**
 * Generate invite token (32 hex chars)
 */
export function generateInviteToken(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Generate referral code (6 alphanumeric chars)
 */
export function generateReferralCode(): string {
  return generateRandomString(6);
}

/**
 * Hash a string using SHA-256
 */
export async function hashString(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ============================================================================
// USER OPERATIONS
// ============================================================================

/**
 * Get all NavigatorX users with optional filtering and pagination
 */
export async function getUsers(options: {
  status?: NavUserSubscriptionStatus;
  searchTerm?: string;
  pageSize?: number;
  lastDoc?: DocumentSnapshot;
  orderByField?: string;
  orderDirection?: 'asc' | 'desc';
} = {}): Promise<PaginatedResult<NavUser>> {
  const {
    status,
    searchTerm,
    pageSize = DEFAULT_PAGE_SIZE,
    lastDoc,
    orderByField = 'createdAt',
    orderDirection = 'desc'
  } = options;

  const constraints: QueryConstraint[] = [
    orderBy(orderByField, orderDirection),
    limit(pageSize + 1) // Get one extra to check if there's more
  ];

  if (status) {
    constraints.unshift(where('subscriptionStatus', '==', status));
  }

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  const q = query(collection(navDb, NAV_USERS_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  // Filter out special documents
  let users = snapshot.docs
    .filter(doc => doc.id !== BLOCKED_DOC && doc.id !== INVITES_DOC && doc.id !== ADMIN_LOGS_DOC)
    .map(doc => ({
      id: doc.id,
      ...doc.data()
    } as NavUser));

  // Client-side search filtering (Firestore doesn't support full-text search)
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    users = users.filter(user =>
      user.email?.toLowerCase().includes(term) ||
      user.displayName?.toLowerCase().includes(term) ||
      user.firstName?.toLowerCase().includes(term) ||
      user.lastName?.toLowerCase().includes(term) ||
      user.phone?.includes(term) ||
      user.company?.toLowerCase().includes(term) ||
      user.referralCode?.toLowerCase().includes(term)
    );
  }

  const hasMore = users.length > pageSize;
  if (hasMore) {
    users = users.slice(0, pageSize);
  }

  return {
    items: users,
    hasMore,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    totalCount: users.length
  };
}

/**
 * Get a single user by ID
 */
export async function getUser(userId: string): Promise<NavUser | null> {
  const docSnap = await getDoc(userDocRef(userId));
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as NavUser;
}

/**
 * Get a user by email
 */
export async function getUserByEmail(email: string): Promise<NavUser | null> {
  const q = query(
    collection(navDb, NAV_USERS_COLLECTION),
    where('email', '==', email.toLowerCase().trim()),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as NavUser;
}

/**
 * Get a user by phone number
 */
export async function getUserByPhone(phone: string): Promise<NavUser | null> {
  const normalizedPhone = phone.replace(/\D/g, '');
  const q = query(
    collection(navDb, NAV_USERS_COLLECTION),
    where('phone', '==', normalizedPhone),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as NavUser;
}

/**
 * Get a user by referral code
 */
export async function getUserByReferralCode(code: string): Promise<NavUser | null> {
  const q = query(
    collection(navDb, NAV_USERS_COLLECTION),
    where('referralCode', '==', code.toUpperCase()),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as NavUser;
}

/**
 * Create a new user (admin-initiated)
 */
export async function createUser(input: NavUserCreateInput, adminId: string): Promise<NavUser> {
  const referralCode = generateReferralCode();
  const now = Timestamp.now();

  const userData: Partial<NavUser> = {
    email: input.email?.toLowerCase().trim() || null,
    phone: input.phone ? '+1' + input.phone.replace(/\D/g, '').slice(-10) : null,
    firstName: input.firstName?.trim() || null,
    lastName: input.lastName?.trim() || null,
    displayName: input.firstName && input.lastName 
      ? `${input.firstName.trim()} ${input.lastName.trim()}` 
      : input.email?.split('@')[0] || 'User',
    company: input.company?.trim() || null,
    subscriptionStatus: input.subscriptionStatus || 'NONE',
    referralCode,
    referralCount: 0,
    totalInvitesSent: 0,
    totalSignups: 0,
    totalCreditsEarned: 0,
    freeMonthsAvailable: 0,
    loginCount: 0,
    favoritesCount: 0,
    hazardsReported: 0,
    hazardsConfirmed: 0,
    hazardPoints: 0,
    notificationsEnabled: true,
    referralNotificationsEnabled: true,
    biometricEnabled: false,
    voiceEnabled: true,
    offlineMapsEnabled: true,
    createdAt: now,
    updatedAt: now,
    createdBy: adminId
  };

  // Set plan dates based on input — only one plan type should be provided.
  // Precedence: trialDays > subscriptionMonths > freeMonths
  if (input.trialDays && input.trialDays > 0) {
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + input.trialDays);
    userData.trialStartDate = now;
    userData.trialEndDate = Timestamp.fromDate(trialEnd);
    userData.subscriptionStatus = 'TRIAL_ACTIVE';
  } else if (input.subscriptionMonths && input.subscriptionMonths > 0) {
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + input.subscriptionMonths);
    userData.subscriptionStatus = 'SUBSCRIBED';
    userData.subscriptionStartDate = now;
    userData.subscriptionEndDate = Timestamp.fromDate(endDate);
    userData.grantedByAdmin = adminId;
    userData.grantedAt = now;
    userData.grantReason = `Admin created with ${input.subscriptionMonths}-month subscription`;
    userData.voiceEnabled = true;
    userData.offlineMapsEnabled = true;
  } else if (input.freeMonths && input.freeMonths > 0) {
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + input.freeMonths);
    userData.subscriptionStatus = 'REFERRAL_FREE_MONTH';
    userData.freeMonthsAvailable = input.freeMonths;
    userData.subscriptionStartDate = now;
    userData.subscriptionEndDate = Timestamp.fromDate(endDate);
    userData.grantedByAdmin = adminId;
    userData.grantedAt = now;
    userData.grantReason = `Admin created with ${input.freeMonths} free month${input.freeMonths > 1 ? 's' : ''}`;
  }

  const docRef = await addDoc(collection(navDb, NAV_USERS_COLLECTION), userData);

  // Log the action
  await logAdminAction(adminId, 'CREATE_USER', docRef.id, {
    email: userData.email,
    subscriptionStatus: userData.subscriptionStatus,
    trialDays: input.trialDays || null,
    subscriptionMonths: input.subscriptionMonths || null,
    freeMonths: input.freeMonths || null
  });

  return { id: docRef.id, ...userData } as NavUser;
}

/**
 * Update an existing user
 */
export async function updateUser(
  userId: string, 
  updates: NavUserUpdateInput, 
  adminId: string
): Promise<NavUser> {
  const userRef = userDocRef(userId);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    throw new Error(`User ${userId} not found`);
  }

  const updateData: Partial<NavUser> = {
    ...updates,
    updatedAt: Timestamp.now()
  };

  // Handle display name update
  if (updates.firstName || updates.lastName) {
    const firstName = updates.firstName || userDoc.data()?.firstName || '';
    const lastName = updates.lastName || userDoc.data()?.lastName || '';
    updateData.displayName = `${firstName} ${lastName}`.trim();
  }

  await updateDoc(userRef, updateData);

  // Log the action
  await logAdminAction(adminId, 'UPDATE_USER', userId, updates);

  const updatedDoc = await getDoc(userRef);
  return { id: updatedDoc.id, ...updatedDoc.data() } as NavUser;
}

/**
 * Delete a user and all their subcollections
 */
export async function deleteUser(userId: string, adminId: string): Promise<void> {
  const batch = writeBatch(navDb);
  
  // Delete all subcollections
  for (const subcollection of Object.values(SUBCOLLECTIONS)) {
    const subRef = userSubcollectionRef(userId, subcollection);
    const subDocs = await getDocs(subRef);
    subDocs.docs.forEach(doc => batch.delete(doc.ref));
  }

  // Delete the user document
  batch.delete(userDocRef(userId));

  await batch.commit();

  // Log the action
  await logAdminAction(adminId, 'DELETE_USER', userId, { permanent: true });
}

/**
 * Suspend a user
 */
export async function suspendUser(userId: string, reason: string, adminId: string): Promise<void> {
  await updateDoc(userDocRef(userId), {
    subscriptionStatus: 'BLOCKED',
    suspendedAt: Timestamp.now(),
    suspendReason: reason,
    suspendedBy: adminId,
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'SUSPEND_USER', userId, { reason });
}

/**
 * Unsuspend a user
 */
export async function unsuspendUser(userId: string, adminId: string): Promise<void> {
  await updateDoc(userDocRef(userId), {
    subscriptionStatus: 'SUBSCRIPTION_EXPIRED', // They'll need to resubscribe
    suspendedAt: null,
    suspendReason: null,
    suspendedBy: null,
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'UNSUSPEND_USER', userId, {});
}

// ============================================================================
// SUBSCRIPTION OPERATIONS
// ============================================================================

/**
 * Extend a user's trial period
 */
export async function extendTrial(
  userId: string, 
  days: number, 
  adminId: string
): Promise<NavUser> {
  const user = await getUser(userId);
  if (!user) throw new Error(`User ${userId} not found`);

  const currentEnd = user.trialEndDate 
    ? timestampToDate(user.trialEndDate) 
    : new Date();
  
  const newEnd = new Date(currentEnd || new Date());
  newEnd.setDate(newEnd.getDate() + days);

  await updateDoc(userDocRef(userId), {
    trialEndDate: Timestamp.fromDate(newEnd),
    subscriptionStatus: 'TRIAL_ACTIVE',
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'EXTEND_TRIAL', userId, { 
    days, 
    newEndDate: newEnd.toISOString() 
  });

  return getUser(userId) as Promise<NavUser>;
}

/**
 * Grant subscription to a user
 */
export async function grantSubscription(
  userId: string, 
  months: number, 
  adminId: string,
  reason?: string
): Promise<NavUser> {
  const user = await getUser(userId);
  if (!user) throw new Error(`User ${userId} not found`);

  const startDate = new Date();
  const endDate = new Date();
  endDate.setMonth(endDate.getMonth() + months);

  await updateDoc(userDocRef(userId), {
    subscriptionStatus: 'SUBSCRIBED',
    subscriptionStartDate: Timestamp.fromDate(startDate),
    subscriptionEndDate: Timestamp.fromDate(endDate),
    grantedByAdmin: adminId,
    grantedAt: Timestamp.now(),
    grantReason: reason || 'Admin granted',
    voiceEnabled: true,
    offlineMapsEnabled: true,
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'GRANT_SUBSCRIPTION', userId, { 
    months, 
    reason,
    endDate: endDate.toISOString() 
  });

  return getUser(userId) as Promise<NavUser>;
}

/**
 * Extend subscription for a user
 */
export async function extendSubscription(
  userId: string, 
  months: number, 
  adminId: string
): Promise<NavUser> {
  const user = await getUser(userId);
  if (!user) throw new Error(`User ${userId} not found`);

  const currentEnd = user.subscriptionEndDate 
    ? timestampToDate(user.subscriptionEndDate) 
    : new Date();
  
  const newEnd = new Date(currentEnd || new Date());
  newEnd.setMonth(newEnd.getMonth() + months);

  await updateDoc(userDocRef(userId), {
    subscriptionEndDate: Timestamp.fromDate(newEnd),
    subscriptionStatus: 'SUBSCRIBED',
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'EXTEND_SUBSCRIPTION', userId, { 
    months, 
    newEndDate: newEnd.toISOString() 
  });

  return getUser(userId) as Promise<NavUser>;
}

/**
 * Cancel subscription for a user
 */
export async function cancelSubscription(
  userId: string, 
  adminId: string,
  reason?: string
): Promise<NavUser> {
  await updateDoc(userDocRef(userId), {
    subscriptionStatus: 'SUBSCRIPTION_EXPIRED',
    cancelledAt: Timestamp.now(),
    cancelledBy: adminId,
    cancelReason: reason || 'Admin cancelled',
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'CANCEL_SUBSCRIPTION', userId, { reason });

  return getUser(userId) as Promise<NavUser>;
}

// ============================================================================
// REFERRAL OPERATIONS
// ============================================================================

/**
 * Get referral statistics for a user
 */
export async function getUserReferralStats(userId: string): Promise<ReferralStats> {
  const user = await getUser(userId);
  if (!user) throw new Error(`User ${userId} not found`);

  // Get referral invites
  const invitesRef = userSubcollectionRef(userId, SUBCOLLECTIONS.REFERRAL_INVITES);
  const invitesSnapshot = await getDocs(invitesRef);

  // Get actual referrals (signups)
  const referralsRef = userSubcollectionRef(userId, SUBCOLLECTIONS.REFERRALS);
  const referralsSnapshot = await getDocs(referralsRef);

  const invites = invitesSnapshot.docs.map(doc => doc.data());
  const referrals = referralsSnapshot.docs.map(doc => doc.data());

  return {
    referralCode: user.referralCode || '',
    totalInvitesSent: invites.length,
    totalSignups: referrals.filter(r => r.signupAt).length,
    totalPaidMonth1: referrals.filter(r => r.month1PaidAt).length,
    totalPaidMonth2: referrals.filter(r => r.month2PaidAt).length,
    totalCreditsEarned: user.totalCreditsEarned || 0,
    freeMonthsAvailable: user.freeMonthsAvailable || 0,
    pendingCredits: referrals.filter(r => r.month1PaidAt && !r.month2PaidAt).length
  };
}

/**
 * Get all referral invites for a user
 */
export async function getUserReferralInvites(userId: string): Promise<ReferralInvite[]> {
  const invitesRef = userSubcollectionRef(userId, SUBCOLLECTIONS.REFERRAL_INVITES);
  const q = query(invitesRef, orderBy('inviteSentAt', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ReferralInvite[];
}

/**
 * Manually grant referral credit to a user
 */
export async function grantReferralCredit(
  userId: string, 
  months: number, 
  adminId: string,
  reason?: string
): Promise<void> {
  await updateDoc(userDocRef(userId), {
    freeMonthsAvailable: increment(months),
    totalCreditsEarned: increment(months),
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'GRANT_REFERRAL_CREDIT', userId, { months, reason });
}

/**
 * Remove referral credit from a user
 */
export async function removeReferralCredit(
  userId: string, 
  months: number, 
  adminId: string,
  reason?: string
): Promise<void> {
  const user = await getUser(userId);
  if (!user) throw new Error(`User ${userId} not found`);

  const newCredits = Math.max(0, (user.freeMonthsAvailable || 0) - months);
  
  await updateDoc(userDocRef(userId), {
    freeMonthsAvailable: newCredits,
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'REMOVE_REFERRAL_CREDIT', userId, { months, reason });
}

/**
 * Regenerate referral code for a user
 */
export async function regenerateReferralCode(
  userId: string, 
  adminId: string
): Promise<string> {
  const newCode = generateReferralCode();
  
  await updateDoc(userDocRef(userId), {
    referralCode: newCode,
    updatedAt: Timestamp.now()
  });

  await logAdminAction(adminId, 'REGENERATE_REFERRAL_CODE', userId, { newCode });

  return newCode;
}

// ============================================================================
// NOTIFICATION OPERATIONS
// ============================================================================

/**
 * Get notifications for a user
 */
export async function getUserNotifications(
  userId: string, 
  limitCount: number = 50
): Promise<any[]> {
  const notifRef = userSubcollectionRef(userId, SUBCOLLECTIONS.NOTIFICATIONS);
  const q = query(notifRef, orderBy('createdAt', 'desc'), limit(limitCount));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

/**
 * Send notification to a user
 */
export async function sendUserNotification(
  userId: string,
  notification: {
    type: string;
    title: string;
    body: string;
    data?: Record<string, any>;
  },
  adminId: string
): Promise<void> {
  const notifRef = userSubcollectionRef(userId, SUBCOLLECTIONS.NOTIFICATIONS);
  
  await addDoc(notifRef, {
    ...notification,
    read: false,
    createdAt: Timestamp.now(),
    sentBy: adminId
  });

  // Increment unread count
  await updateDoc(userDocRef(userId), {
    unreadNotificationCount: increment(1)
  });

  await logAdminAction(adminId, 'SEND_NOTIFICATION', userId, { 
    type: notification.type, 
    title: notification.title 
  });
}

/**
 * Send push notification via FCM (calls Cloud Function)
 */
export async function sendPushNotification(
  userId: string,
  title: string,
  body: string,
  data?: Record<string, any>,
  adminId?: string
): Promise<void> {
  try {
    const sendPush = httpsCallable(functions, 'sendNavigatorPushNotification');
    await sendPush({ userId, title, body, data });
    
    if (adminId) {
      await logAdminAction(adminId, 'SEND_PUSH_NOTIFICATION', userId, { title });
    }
  } catch (error) {
    console.error('Failed to send push notification:', error);
    throw error;
  }
}

// ============================================================================
// HAZARD OPERATIONS
// ============================================================================

/**
 * Get all active hazards
 */
export async function getActiveHazards(options: {
  type?: HazardType;
  pageSize?: number;
} = {}): Promise<Hazard[]> {
  const constraints: QueryConstraint[] = [
    where('isActive', '==', true),
    orderBy('reportedAt', 'desc')
  ];

  if (options.type) {
    constraints.unshift(where('type', '==', options.type));
  }

  if (options.pageSize) {
    constraints.push(limit(options.pageSize));
  }

  const q = query(collection(navDb, HAZARDS_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Hazard[];
}

/**
 * Get hazard by ID
 */
export async function getHazard(hazardId: string): Promise<Hazard | null> {
  const docSnap = await getDoc(doc(navDb, HAZARDS_COLLECTION, hazardId));
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as Hazard;
}

/**
 * Approve a hazard
 */
export async function approveHazard(hazardId: string, adminId: string): Promise<void> {
  await updateDoc(doc(navDb, HAZARDS_COLLECTION, hazardId), {
    isActive: true,
    approvedAt: Timestamp.now(),
    approvedBy: adminId
  });

  await logAdminAction(adminId, 'APPROVE_HAZARD', hazardId, {});
}

/**
 * Reject/Delete a hazard
 */
export async function deleteHazard(hazardId: string, adminId: string, reason?: string): Promise<void> {
  await deleteDoc(doc(navDb, HAZARDS_COLLECTION, hazardId));
  await logAdminAction(adminId, 'DELETE_HAZARD', hazardId, { reason });
}

/**
 * Force expire a hazard
 */
export async function expireHazard(hazardId: string, adminId: string): Promise<void> {
  await updateDoc(doc(navDb, HAZARDS_COLLECTION, hazardId), {
    isActive: false,
    expiredAt: Timestamp.now(),
    expiredBy: adminId
  });

  await logAdminAction(adminId, 'EXPIRE_HAZARD', hazardId, {});
}

// ============================================================================
// WAITLIST OPERATIONS
// ============================================================================

/**
 * Get all waitlist entries
 */
export async function getWaitlistEntries(options: {
  state?: string;
  converted?: boolean;
  pageSize?: number;
} = {}): Promise<WaitlistEntry[]> {
  const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];

  if (options.state) {
    constraints.unshift(where('stateName', '==', options.state));
  }

  if (options.converted !== undefined) {
    constraints.unshift(where('convertedToUser', '==', options.converted));
  }

  if (options.pageSize) {
    constraints.push(limit(options.pageSize));
  }

  const q = query(collection(navDb, WAITLIST_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as WaitlistEntry[];
}

/**
 * Delete waitlist entry
 */
export async function deleteWaitlistEntry(entryId: string, adminId: string): Promise<void> {
  await deleteDoc(doc(navDb, WAITLIST_COLLECTION, entryId));
  await logAdminAction(adminId, 'DELETE_WAITLIST_ENTRY', entryId, {});
}

/**
 * Mark waitlist entry as notified
 */
export async function markWaitlistNotified(entryId: string, adminId: string): Promise<void> {
  await updateDoc(doc(navDb, WAITLIST_COLLECTION, entryId), {
    notified: true,
    notifiedAt: Timestamp.now(),
    notifiedBy: adminId
  });

  await logAdminAction(adminId, 'NOTIFY_WAITLIST', entryId, {});
}

// ============================================================================
// BLOCKED IDENTITIES OPERATIONS
// ============================================================================

/**
 * Block an email address
 */
export async function blockEmail(
  email: string, 
  reason: string, 
  adminId: string
): Promise<void> {
  const emailHash = await hashString(email);
  
  await setDoc(doc(blockedEmailsRef(), emailHash), {
    emailHash,
    reason,
    blockedAt: Timestamp.now(),
    blockedBy: adminId
  });

  await logAdminAction(adminId, 'BLOCK_EMAIL', emailHash, { reason });
}

/**
 * Unblock an email address
 */
export async function unblockEmail(emailHash: string, adminId: string): Promise<void> {
  await deleteDoc(doc(blockedEmailsRef(), emailHash));
  await logAdminAction(adminId, 'UNBLOCK_EMAIL', emailHash, {});
}

/**
 * Block a phone number
 */
export async function blockPhone(
  phone: string, 
  reason: string, 
  adminId: string
): Promise<void> {
  const phoneHash = await hashString(phone);
  
  await setDoc(doc(blockedPhonesRef(), phoneHash), {
    phoneHash,
    reason,
    blockedAt: Timestamp.now(),
    blockedBy: adminId
  });

  await logAdminAction(adminId, 'BLOCK_PHONE', phoneHash, { reason });
}

/**
 * Unblock a phone number
 */
export async function unblockPhone(phoneHash: string, adminId: string): Promise<void> {
  await deleteDoc(doc(blockedPhonesRef(), phoneHash));
  await logAdminAction(adminId, 'UNBLOCK_PHONE', phoneHash, {});
}

/**
 * Get all blocked emails
 */
export async function getBlockedEmails(): Promise<any[]> {
  const snapshot = await getDocs(blockedEmailsRef());
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get all blocked phones
 */
export async function getBlockedPhones(): Promise<any[]> {
  const snapshot = await getDocs(blockedPhonesRef());
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// ============================================================================
// ANALYTICS & STATS
// ============================================================================

/**
 * Get NavigatorX overall statistics
 */
export async function getNavigatorXStats(): Promise<NavigatorXStats> {
  const usersSnapshot = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
  
  // Filter out special documents
  const users = usersSnapshot.docs
    .filter(doc => doc.id !== BLOCKED_DOC && doc.id !== INVITES_DOC && doc.id !== ADMIN_LOGS_DOC)
    .map(doc => doc.data());

  const hazardsSnapshot = await getDocs(
    query(collection(navDb, HAZARDS_COLLECTION), where('isActive', '==', true))
  );

  const waitlistSnapshot = await getDocs(
    query(collection(navDb, WAITLIST_COLLECTION), where('convertedToUser', '==', false))
  );

  // Calculate stats
  const totalUsers = users.length;
  const activeSubscribers = users.filter(u => 
    u.subscriptionStatus === 'SUBSCRIBED' || u.subscriptionStatus === 'active'
  ).length;
  const trialUsers = users.filter(u => 
    u.subscriptionStatus === 'TRIAL_ACTIVE' || u.subscriptionStatus === 'trial'
  ).length;
  const expiredUsers = users.filter(u => 
    ['TRIAL_EXPIRED', 'SUBSCRIPTION_EXPIRED', 'expired', 'cancelled'].includes(u.subscriptionStatus)
  ).length;
  const invitedUsers = users.filter(u => u.subscriptionStatus === 'invited').length;

  const totalLogins = users.reduce((sum, u) => sum + (u.loginCount || 0), 0);
  const totalFavorites = users.reduce((sum, u) => sum + (u.favoritesCount || 0), 0);
  // Android writes hazardPoints, web writes hazardsReported — same metric, different field names.
  // Use Math.max to avoid double-counting if both fields are populated on the same user.
  const totalHazardsReported = users.reduce((sum, u) => sum + Math.max(u.hazardsReported || 0, u.hazardPoints || 0), 0);
  const totalReferrals = users.reduce((sum, u) => sum + Math.max(u.referralCount || 0, u.totalInvitesSent || 0), 0);

  const mrr = activeSubscribers * SUBSCRIPTION_PRICE;
  const arr = mrr * 12;

  return {
    totalUsers,
    activeSubscribers,
    trialUsers,
    expiredUsers,
    invitedUsers,
    mrr,
    arr,
    totalLogins,
    totalFavorites,
    totalHazardsReported,
    totalReferrals,
    activeHazards: hazardsSnapshot.size,
    waitlistCount: waitlistSnapshot.size,
    conversionRate: totalUsers > 0 ? (activeSubscribers / totalUsers) * 100 : 0,
    avgLoginsPerUser: totalUsers > 0 ? totalLogins / totalUsers : 0
  };
}

// ============================================================================
// AUDIT LOGGING
// ============================================================================

/**
 * Log an admin action for audit purposes
 */
export async function logAdminAction(
  adminId: string,
  action: AdminAction,
  targetId: string,
  details: Record<string, any>
): Promise<void> {
  try {
    await addDoc(adminLogsRef(), {
      adminId,
      action,
      targetId,
      details,
      timestamp: Timestamp.now(),
      ipAddress: null // Would need to be passed from client
    });
  } catch (error) {
    console.error('Failed to log admin action:', error);
    // Don't throw - logging failure shouldn't block the operation
  }
}

/**
 * Get admin audit logs
 */
export async function getAdminLogs(options: {
  adminId?: string;
  action?: AdminAction;
  targetId?: string;
  startDate?: Date;
  endDate?: Date;
  pageSize?: number;
} = {}): Promise<AdminAuditLog[]> {
  const constraints: QueryConstraint[] = [
    orderBy('timestamp', 'desc')
  ];

  if (options.adminId) {
    constraints.unshift(where('adminId', '==', options.adminId));
  }

  if (options.action) {
    constraints.unshift(where('action', '==', options.action));
  }

  if (options.targetId) {
    constraints.unshift(where('targetId', '==', options.targetId));
  }

  if (options.pageSize) {
    constraints.push(limit(options.pageSize));
  }

  const q = query(adminLogsRef(), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as AdminAuditLog[];
}

// ============================================================================
// REAL-TIME SUBSCRIPTIONS
// ============================================================================

/**
 * Subscribe to real-time user updates
 */
export function subscribeToUsers(
  callback: (users: NavUser[]) => void
): Unsubscribe {
  const q = query(
    collection(navDb, NAV_USERS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(100)
  );

  return onSnapshot(q, (snapshot) => {
    const users = snapshot.docs
      .filter(doc => doc.id !== BLOCKED_DOC && doc.id !== INVITES_DOC && doc.id !== ADMIN_LOGS_DOC)
      .map(doc => ({ id: doc.id, ...doc.data() } as NavUser));
    callback(users);
  });
}

/**
 * Subscribe to real-time hazard updates
 */
export function subscribeToHazards(
  callback: (hazards: Hazard[]) => void
): Unsubscribe {
  const q = query(
    collection(navDb, HAZARDS_COLLECTION),
    where('isActive', '==', true),
    orderBy('reportedAt', 'desc'),
    limit(100)
  );

  return onSnapshot(q, (snapshot) => {
    const hazards = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Hazard));
    callback(hazards);
  });
}

// ============================================================================
// EXPORT DEFAULT SERVICE OBJECT
// ============================================================================

const navigatorXService = {
  // Constants
  NAV_USERS_COLLECTION,
  HAZARDS_COLLECTION,
  WAITLIST_COLLECTION,
  SUBCOLLECTIONS,
  SUBSCRIPTION_STATUS,

  // Helpers
  userDocRef,
  userSubcollectionRef,
  timestampToDate,
  normalizeSubscriptionStatus,
  generateInviteToken,
  generateReferralCode,
  hashString,

  // Users
  getUsers,
  getUser,
  getUserByEmail,
  getUserByPhone,
  getUserByReferralCode,
  createUser,
  updateUser,
  deleteUser,
  suspendUser,
  unsuspendUser,

  // Subscriptions
  extendTrial,
  grantSubscription,
  extendSubscription,
  cancelSubscription,

  // Referrals
  getUserReferralStats,
  getUserReferralInvites,
  grantReferralCredit,
  removeReferralCredit,
  regenerateReferralCode,

  // Notifications
  getUserNotifications,
  sendUserNotification,
  sendPushNotification,

  // Hazards
  getActiveHazards,
  getHazard,
  approveHazard,
  deleteHazard,
  expireHazard,

  // Waitlist
  getWaitlistEntries,
  deleteWaitlistEntry,
  markWaitlistNotified,

  // Blocked Identities
  blockEmail,
  unblockEmail,
  blockPhone,
  unblockPhone,
  getBlockedEmails,
  getBlockedPhones,

  // Analytics
  getNavigatorXStats,

  // Audit
  logAdminAction,
  getAdminLogs,

  // Real-time
  subscribeToUsers,
  subscribeToHazards
};

export default navigatorXService;
